// frontend/src/design-system/motion.ts
import type { Variants, Transition } from 'framer-motion';

/** Smooth, opinionated spring used for dialog opens + page transitions. */
export const SPRING_SOFT: Transition = { type: 'spring', stiffness: 280, damping: 28, mass: 1 };

/** Faster spring for short-distance moves (status pills). */
export const SPRING_SNAPPY: Transition = { type: 'spring', stiffness: 380, damping: 30 };

/** Standard fade + slide-up used for route transitions. */
export const PAGE_TRANSITION: Variants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0, transition: { ...SPRING_SOFT, duration: 0.24 } },
  exit:    { opacity: 0, y: -4, transition: { duration: 0.12 } },
};

/**
 * Refined editorial-Apple page transition: slightly softer slide, longer exit,
 * spring-based position with eased opacity. Use this for the new shells.
 * `PAGE_TRANSITION` is kept exported for any non-shell consumers.
 *
 * Shape mirrors `PAGE_TRANSITION` (a `Variants` object consumed via the
 * `variants={...}` prop on `motion.div`), so swapping is import-rename only.
 */
export const PAGE_TRANSITION_V2: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 26,
      mass: 0.9,
      opacity: { duration: 0.28, ease: [0.2, 0.65, 0.3, 1] },
    },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: {
      type: 'spring',
      stiffness: 260,
      damping: 26,
      mass: 0.9,
      opacity: { duration: 0.18, ease: [0.4, 0, 0.6, 1] },
    },
  },
};

/** Status pill mount animation. */
export const PILL_MOUNT: Variants = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1,   transition: SPRING_SNAPPY },
};

/** Dialog content motion (used inside DialogContent override if we ever wrap it). */
export const DIALOG_CONTENT: Variants = {
  initial: { opacity: 0, scale: 0.96, y: -8 },
  animate: { opacity: 1, scale: 1,    y: 0, transition: SPRING_SOFT },
  exit:    { opacity: 0, scale: 0.97, y: -6, transition: { duration: 0.14 } },
};
